// Every propane fuelchoice variant — including the legacy 'lp' value with no tank/inline
// suffix — must resolve to LP. Anything not in this set (ng, none, '', unset) is not propane.
const PROPANE_FUEL_VALUES = new Set(['lp', 'lp-tank', 'lp-inline']);

// Values meaning "no fuel selected" — legacy data used '<none>' as the unset sentinel.
const NO_FUEL_VALUES = new Set(['', 'none', '<none>', 'null', 'undefined']);

/**
 * Whether the given kitchen fuelchoice is any propane variant (lp / lp-tank / lp-inline).
 * @param fuelchoice the kitchen-level fuelchoice value
 * @returns true when the fuel is propane
 */
export const isPropaneFuel = (fuelchoice: string | null | undefined): boolean => {
	return PROPANE_FUEL_VALUES.has((fuelchoice ?? '').trim().toLowerCase());
};

/**
 * Returns the appliance SKU fuel suffix ('LP' or 'NG') for the given kitchen fuelchoice.
 * @param fuelchoice the kitchen-level fuelchoice value
 * @returns 'LP' for any propane variant, otherwise 'NG'
 */
export const getFuelSkuSuffix = (fuelchoice: string | null | undefined): 'LP' | 'NG' => {
	return isPropaneFuel(fuelchoice) ? 'LP' : 'NG';
};

/**
 * Whether the kitchen is EXPLICITLY natural gas. Unlike getFuelSkuSuffix — which falls
 * back to 'NG' for an unset kitchen — a no-fuel kitchen is not natural gas here, so
 * NG-only copy never prints on a kitchen whose fuel source reads 'Not Applicable'.
 * @param fuelchoice the kitchen-level fuelchoice value
 * @returns true when the fuel is natural gas
 */
export const isNaturalGasFuel = (fuelchoice: string | null | undefined): boolean => {
	if (NO_FUEL_VALUES.has((fuelchoice ?? '').trim().toLowerCase())) return false;
	return getFuelSkuSuffix(fuelchoice) === 'NG';
};

/**
 * Single source of truth for the user-facing fuel-source label. Always reflects the
 * actually-set value — a no-fuel/unset kitchen renders 'Not Applicable', never a
 * silent 'Natural Gas' fallback.
 * @param fuelchoice the kitchen-level fuelchoice value
 * @returns the display label ('Natural Gas' / 'Liquid Propane | Tank' / 'Not Applicable' / …)
 */
export const getFuelDisplayLabel = (fuelchoice: string | null | undefined): string => {
	const normalized = (fuelchoice ?? '').trim().toLowerCase();
	if (NO_FUEL_VALUES.has(normalized)) return 'Not Applicable';

	switch (normalized) {
		case 'ng': return 'Natural Gas';
		case 'lp': return 'Liquid Propane';
		case 'lp-tank': return 'Liquid Propane | Tank';
		case 'lp-inline': return 'Liquid Propane | Inline';
		default: return fuelchoice ?? 'Not Applicable';
	}
};
